/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import 'gmf/controllers/desktop.scss';
import './sass/light.scss';

import angular from 'angular';
import * as olEasing from 'ol/easing.js';
import olMap from 'ol/Map.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olFeature from 'ol/Feature.js';
import olLayerVector from 'ol/layer/Vector.js';
import olStyleText from 'ol/style/Text.js';
import olGeomPoint from 'ol/geom/Point.js';
import olCollection from 'ol/Collection.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleCircle from 'ol/style/Circle.js';
import olLayerGroup from 'ol/layer/Group.js';

import ngeoFeatureOverlayMgr from 'ngeo/map/FeatureOverlayMgr';

//Load interface
import gmfControllersAbstractDesktopController, {
    AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController.js';

// Load Components
import gmfQueryWindowComponent from 'gmf/query/windowComponent.js';

/**
 * @private
 */
class Controller extends AbstractDesktopController {
    /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {angular.$q} $q
   * @param {ngeo.CreatePrint} ngeoCreatePrint The ngeo Create Print function.
   * @param {ngeo.LayerHelper} ngeoLayerHelper The ngeo Layer Helper service.
   * @param {ngeo.PrintUtils} ngeoPrintUtils The ngeo PrintUtils service.
   * @param {gmf.Themes} gmfThemes The gmf Themes service.
   * @param {ngeo.Disclaimer} ngeoDisclaimer
   * @param {gmf.FulltextSearchService} gmfSearchFulltextSearch
   * @param {angular.ITimeoutService} $timeout Angular timeout service.
   * @ngInject
   */
    constructor(
        $scope,
        $injector,
        $q,
        ngeoPrintUtils,
        ngeoCreatePrint,
        ngeoLayerHelper,
        gmfThemes,
        gmfSearchFulltextSearch,
        ngeoDisclaimer,
        $timeout
    ) {
        super($scope,$injector);

        /**
     * @type {angular.IScope}
     * @private
     */
        this.scope = $scope;

        /**
     * @param {angular.auto.IInjectorService}
     * @private
     */
        this.$injector = $injector;

        /**
     * @type {angular.ITimeoutService}
     * @private
     */
        this.timeout_ = $timeout;

        /**
     * @type {?string}
     */
        this.displaywindowWidth = '32rem';

        /**
     * @type {?string}
     */
        this.displaywindowHeight = '20rem';

        /**
     * @type {olCollection}
     * @const
     * @protected
     */
        this.featureIcons_ = new olCollection();

        /**
     * @type {olSourceVector}
     * @const
     * @private
     */
        this.vectorSource_ = new olSourceVector({
            features: this.featureIcons_,
        });

        /**
     * @type {olLayerVector}
     * @const
     * @private
     */
        this.vectorLayer_ = new olLayerVector({
            source: this.vectorSource_,
        });

        // add layer to map
        this.map.addLayer(this.vectorLayer_);

        /**
     * @type {!Array<number>}
     */
        this.scaleSelectorValues = this.$injector.get('ngeoScaleSelectorOptions');

        //this.EPSG2056 = EPSG2056;

        /**
     * Load Icons
     * @type {Object.<string, string>}
     * @private
     */
        this.imgList = this.importAll(
            require.context('./images/icons/', false, /\.(png)$/)
        );

        // Allow angular-gettext-tools to collect the strings to translate
        /**
     * @type {angularGettext.Catalog}
     */
        const gettextCatalog = $injector.get('gettextCatalog');
        gettextCatalog.getString('Thema hinzufügen');
        gettextCatalog.getString('Karte hinzufügen');
        gettextCatalog.getString('Kartenebene hinzufügen');

        const recenterIcon = this.ngeoLocation.getParam('recenter_icon');
        const mapX = this.ngeoLocation.getParam('map_x');
        const mapY = this.ngeoLocation.getParam('map_y');
        if (recenterIcon && mapX && mapY) {
            this.addFeatureIcon(
                recenterIcon,
                /** @type {olCoordinate} */ ([mapX, mapY])
            );
            this.fitMapToFeatures();
        }

        const paramIcons = this.ngeoLocation.getParam('featureIcons');
        if (paramIcons) {
            const icons = this.parseFeatureList(paramIcons);

            for (const icon of Object.keys(icons)) {
                for (const coord of icons[icon]) {
                    this.addFeatureIcon(icon, coord);
                }
            }
            this.timeout_(() => {
                this.fitMapToFeatures();
            });
        }

        /**
     * list of disabled features
     * @type {Object.<string, boolean>}
     */
        this.disabled = {
            // available in the html: login, print, draw, filter, editFeature, drawProfile, googleStreetView
            // map-features to disable: navigation, zoom, interactions
            // zoom: true,
            // navigation: true,
            // print: true
        };

        // // update list of disabled features from query string
        // // example: ?disabled=login,print
        const paramDisabled = this.ngeoLocation.getParam('disabled') || '';
        paramDisabled.split(',').forEach(function (disableFeature) {
            this.disabled[disableFeature] = true;
        }, this);

        // remove map interactions
        if (this.disabled.interactions || this.disabled.navigation) {
            this.map.getInteractions().clear();
        }

        /**
     * @type {boolean}
     */
        this.dataPanelActive = true;

        // if there are disabled elements, resize the map
        // because the available space may have changed
        if (Object.keys(this.disabled).length > 0) {
            this.timeout_(() => {
                this.map.updateSize();
            });
        }

        /**
     * @type {string}
     */
        this.environmentName = this.$injector.get('environmentName');

        /**
     * @type {gmf.FulltextSearchService}
     * @private
     */
        this.fullTextSearchService_ = gmfSearchFulltextSearch;

        /**
     * @type {boolean}
     * @private
     */
        this.searchIsReady = false;

        /**
     *
     * Initialize Print variables
     *
     */

        this.q = $q;

        /** @type {string} */
        this.printState = 'ready';

        /**
     * @type {?angular.$q.Deferred}
     * @private
     */
        this.requestCanceler = null;

        /**
     * @type {string}
     * @private
     */
        this.curRef;

        this.gmfPrintUrl = $injector.get('gmfPrintUrl');

        /** @type {ngeo.PrintUtils} */
        this.ngeoPrintUtils = ngeoPrintUtils;

        this.ngeoPrint = ngeoCreatePrint(this.gmfPrintUrl);

        /** @type {olSize} */
        this.paperSize = [511, 685];

        this.ngeoLayerHelper = ngeoLayerHelper;

        /**
     * @type {olLayerVector}
     * @private
     */
        this.featureOverlayLayer_ = ngeoFeatureOverlayMgr.getLayer();

        this.ogcServers = [];
        gmfThemes.getOgcServersObject().then((ogcServersObject) => {
            this.ogcServers = ogcServersObject;
        });

        this.disclaimerService = ngeoDisclaimer;
    }


    /**
   * Opens the current view in a new window in the desktop app.
   */
    openInDesktopApp() {
        const location = this.ngeoLocation.getUriString().split('?');
        const params = location[1] || '';
        const desktopURI = `/?${params}`;
        window.open(desktopURI);
    }

    /**
   * Import all Images/Cons path
   */
    importAll(r) {
        const images = {};
        r.keys().map((item) => {
            images[item.replace('./', '')] = r(item);
        });
        return images;
    }

    /**
   * Parses the featureList parameter into an map of features.
   * Icon file List: imgList
   * Format:
   * <icon-filename>:<x-value>,<y-value>;<x-value>,<y-value>;...|<icon-filename>:<x-value>,<y-value>|...
   *
   * Example:
   * icon1.png:1,2;3,4|folder/icon2.png:3,4|icon3.png:6,7;8,9
   *
   * Returns:
   * {
   *  'iconFileName' : [ [x,y], [x,y] ],
   *  'anotherFileName' : [ [x,y], [x,y] ]
   * }
   *
   * @param {string} encodedFeatureList
   * @returns {!Object.<string, !Array.<olCoordinate>>} map of icons an their coordinates
   */
    parseFeatureList(encodedFeatureList) {
        const iconGroups = encodedFeatureList.split('|');
        const features = {};
        for (const iconGroup of iconGroups) {
            const tmp = iconGroup.split(':');
            if (tmp.length !== 2) {
                continue;
            }
            const iconName = tmp[0];
            const coords = tmp[1]
        .split(';')
        .map(function (coordString) {
            return coordString
            .split(',') //convert to array
            .map(parseFloat) // cast to float
            .filter(isFinite); // filter out non-numbers
        })
        .filter((c) => c.length === 2); // only allow coordinates with two elements
            if (coords.length > 0) {
                features[iconName] = coords;
            }
        }
        return features;
    }

    /**
   * @param {string} icon Icon name.
   * @param {olCoordinate} coordinate Coordinate of icon.
   * @private
   */
    addFeatureIcon(icon, coordinate) {
        const iconFullPath = this.imgList[icon];
        const feature = new olFeature({
            name: icon,
            geometry: new olGeomPoint(coordinate),
        });
        feature.setStyle(
            new olStyleStyle({
                image: new olStyleIcon({
                    src: iconFullPath, // set image path to parameter value
                    anchor: [0.5, 1],
                    scale: 0.5,
                }),
            })
        );
        this.featureIcons_.push(feature);
    }

    /**
   * Center map on features in this.featureIcons_
   */
    fitMapToFeatures() {
        this.map.getView().fit(this.vectorSource_.getExtent(), {maxZoom: 8});
    }

    /**
   * start print of current map
   */
    startPrint() {
        if (this.printState === 'printing') {
            return; // TODO cancel print request
        }

        this.printState = 'printing';
        this.requestCanceler = this.q.defer();

        const printSpec = this.preparePrintSpec();
        console.log(printSpec);

        this.ngeoPrint
      .createReport(
          printSpec,
          /** @type {angular.$http.Config} */ ({
              timeout: this.requestCanceler.promise,
          })
      )
      .then(
          this.handleCreateReportSuccess.bind(this),
          this.handleCreateReportError.bind(this)
      );
    }

    /**
   * creates the needed print spec
   * @returns {MapFishPrintSpec}
   * @private
   */
    preparePrintSpec() {
        const viewResolution = this.map.getView().getResolution() | 256;
        const mapZoom = this.map.getView().getZoom();
        // assertNumber(mapZoom);
        let scale = this.scaleSelectorValues.values[Math.round(mapZoom)];
        if (!scale) {
            scale = 500;
        }

        const customAttributes = {
            'datasource': [],
            'lang': 'de',
            'comments': '',
            'title': ''
        };

        const spec = this.ngeoPrint.createSpec(
            this.map,
            scale,
            0, //rotation
            256, //dpi
            'print_A4_hochformat', //layout
            'pdf', //format
            customAttributes
        );

        const layers = [];
        this.ngeoPrint.encodeLayer(
            layers,
            this.featureOverlayLayer_,
            viewResolution
        );
        if (layers.length > 0) {
            spec.attributes.map.layers.unshift(layers[0]);
        }

        return spec;
    }

    /**
   * handles successfully start response
   * @param {*} resp
   * @private
   */
    handleCreateReportSuccess(resp) {
        const mfResp =
        /** @type {import('ngeo/print/mapfish-print-v3').MapFishPrintReportResponse} */ (
                resp.data
            );
        const ref = mfResp.ref;
        console.assert(ref.length > 0);
        this.curRef = ref;
        this.getPrintState();
    }

    /**
   * gets print state of current print and act correspondly
   * @private
   */
    getPrintState() {
        this.requestCanceler = this.q.defer();
        this.ngeoPrint
      .getStatus(
          this.curRef,
          /** @type {angular.$http.Config} */ ({
              timeout: this.requestCanceler.promise,
          })
      )
      .then(
          this.handleGetStatusSuccess.bind(this),
          this.handleCreateReportError.bind(this)
      );
    }

    /**
   * @param {!angular.$http.Response} resp Response.
   * @private
   */
    handleGetStatusSuccess(resp) {
        const mfResp = /** @type {MapFishPrintStatusResponse} */ (resp.data);
        const done = mfResp.done;
        if (done) {
            if (mfResp.status !== 'error') {
                this.printState = 'ready';
                // The report is ready. Open it by changing the window location.
                window.location.href = this.ngeoPrint.getReportUrl(this.curRef);
            } else {
                console.error(mfResp.error);
                this.printState = 'ready';
            }
        } else {
            // The report is not ready yet. Check again in 1s.
            this.statusTimeoutPromise_ = this.timeout_(
                () => {
                    this.getPrintState();
                },
                1000,
                false
            );
        }
    }

    /**
   * @private
   */
    handleCreateReportError() {
        this.printState = 'ready';
        console.log('Fatal Error. May the force be with you!');
    }

    zoomIn() {
        if(this.map.getView().getResolution() > this.map.getView().minResolution_) {
            this.zoom(1);
        }
    }

    zoomOut() {
        if(this.map.getView().getResolution() < this.map.getView().maxResolution_) {
            this.zoom(-1);
        }
    }

    /**
   * @private
   */
    zoom(step) {
        const view = this.map.getView();
        const currentResolution = view.getResolution();
        if (currentResolution) {
            const newResolution = view.resolutions_[view.resolutions_.findIndex(x => x === currentResolution) + step];
            view.setResolution(newResolution);
        }
    }
    /**
   * Laden der Karte auf Basis der Suche.
   */
    onGmfSearchReady() {
    //es gibt ein infinit-Loop, wenn diese Funktion durch gmf-search-on-init aufgerfuen wird. Durch dieses If-Statment kann wird alles nur einmal Aufgerufen.
        if (!this.searchIsReady) {
            const query = this.ngeoLocation.getParam('recenter_search');
            this.searchIsReady = true;
            if (query) {
                let resultIndex = 1;
                if (this.ngeoLocation.getParam('recenter_search_index')) {
                    resultIndex = parseInt(
                        this.ngeoLocation.getParam('recenter_search_index'),
                        10
                    );
                }
                if (resultIndex < 1) {
                    // can't be lower than one
                    resultIndex = 1;
                }

                this.fullTextSearchService_
          .search(query, {'limit': resultIndex})
          .then((data) => {
              if (data && data.features[resultIndex - 1]) {
                  const format = new olFormatGeoJSON();
                  const feature = format.readFeature(
                      data.features[resultIndex - 1]
                  );
                  const geometry = feature.getGeometry();
                  const center = this.map.getView().getCenter(geometry.getExtent());

                  // reset crosshair if needed
                  if (this.ngeoLocation.getParam('map_crosshair')) {
                      this.gmfPermalink_.setMapCrosshair(center);
                  }

                  // reset tooltip if needed
                  const tooltip = this.ngeoLocation.getParam('map_tooltip');
                  if (tooltip) {
                      this.gmfPermalink_.setMapTooltip(tooltip, center);
                  }

                  // set recenterIcon
                  const recenterIcon = this.ngeoLocation.getParam('recenter_icon');
                  if (recenterIcon) {
                      this.addFeatureIcon(
                          recenterIcon,
                          /** @type {olCoordinate} */ center
                      );
                  }

                  const fitOptions = /** @type {olView.FitOptions} */ ({});
                  if (this.ngeoLocation.hasParam('map_zoom')) {
                      fitOptions.maxZoom = parseInt(
                          this.ngeoLocation.getParam('map_zoom'),
                          10
                      );
                      // Enforce boundaries: 0 <= maxZoom <= 8
                      fitOptions.maxZoom = Math.max(
                          Math.min(fitOptions.maxZoom, 8),
                          0
                      );
                  }

                  // center map on search result
                  this.map
                .getView()
                .fit(feature.getGeometry().getExtent(), fitOptions);
              }
          });
            }
        }
    }
}

/**
 * @hidden
 */
const moduleLight = angular.module('Applight', [
    gmfControllersAbstractDesktopController.name,
    gmfQueryWindowComponent.name,
]);

moduleLight.controller('LightController', Controller);

moduleLight.value('gmfLayertreeOptions', {
    'legendIcon': {'width': 'auto', 'height': 'auto'},
});

export default moduleLight;
